import { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  query,
  where
} from "firebase/firestore";
import { db } from "../../firebase";
import { buildStockRows, defectiveRows } from "../../utils/stockDocs";

export default function DefectiveStockDashboard() {
  const [materials, setMaterials] = useState([]);
  const [stockInfo, setStockInfo] = useState({ attention: [], junkDocs: 0, inactiveWithStock: { items: 0, qty: 0 } });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDefectiveStock();
  }, []);

  const fetchDefectiveStock = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const companyId = user.company_id || "";
      if (!companyId) {
        alert("Company ID not found. Please log in again.");
        return;
      }

      // P4-8: real stock docs of active items only (stockDocs.js). Junk docs
      // used to show here as "Healthy" lines.
      const [itemsSnap, stockSnap] = await Promise.all([
        getDocs(
          query(
            collection(db, "items"),
            where("company_id", "in", [companyId, ""])
          )
        ),
        getDocs(
          query(
            collection(db, "inventory_summary"),
            where("company_id", "==", companyId)
          )
        ),
      ]);

      const stock = buildStockRows({
        items: itemsSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
        stockDocs: stockSnap.docs.map((d) => ({ id: d.id, data: d.data() })),
        companyId,
      });

      setMaterials(defectiveRows(stock.rows));
      setStockInfo(stock);
    } catch (error) {
      console.error("Defective stock fetch failed:", error);
      alert("Failed to load defective stock dashboard");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (defective, scrap) => {
    if ((scrap || 0) > 0) {
      return "bg-red-100 text-red-700";
    }

    if ((defective || 0) > 0) {
      return "bg-yellow-100 text-yellow-700";
    }

    return "bg-green-100 text-green-700";
  };

  const getStatusText = (defective, scrap) => {
    if ((scrap || 0) > 0) return "Scrap";
    if ((defective || 0) > 0) return "Defective";
    return "Healthy";
  };

  if (loading) {
    return (
      <div className="p-6 text-lg font-medium">
        Loading Defective Stock Dashboard...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 p-6">
      <div className="max-w-7xl mx-auto bg-white rounded-2xl shadow-lg border p-6">

        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-bold text-gray-800">
            Defective / Scrap Stock Dashboard
          </h2>

          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium"
          >
            ← Back
          </button>
        </div>

        {/* ✅ Summary Cards — top pe */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-green-50 rounded-xl p-4 border">
            <div className="text-sm text-gray-600">Healthy Items</div>
            <div className="text-2xl font-bold text-green-700">
              {materials.filter(x => (x.defective_stock || 0) === 0 && (x.scrap_stock || 0) === 0).length}
            </div>
          </div>
          <div className="bg-yellow-50 rounded-xl p-4 border">
            <div className="text-sm text-gray-600">Defective Items</div>
            <div className="text-2xl font-bold text-yellow-700">
              {materials.filter(x => (x.defective_stock || 0) > 0).length}
            </div>
          </div>
          <div className="bg-red-50 rounded-xl p-4 border">
            <div className="text-sm text-gray-600">Scrap Items</div>
            <div className="text-2xl font-bold text-red-700">
              {materials.filter(x => (x.scrap_stock || 0) > 0).length}
            </div>
          </div>
          <div className="bg-blue-50 rounded-xl p-4 border">
            <div className="text-sm text-gray-600">Total Inventory Lines</div>
            <div className="text-2xl font-bold text-blue-700">
              {materials.length}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto rounded-xl border">
          <table className="w-full border-collapse">

            <thead>
              <tr className="bg-slate-800 text-white">
                <th className="p-3 text-left">Item</th>
                <th className="p-3 text-left">Good Stock</th>
                <th className="p-3 text-left">Defective Stock</th>
                <th className="p-3 text-left">Scrap Stock</th>
                <th className="p-3 text-left">Total Stock</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-left">Last Updated</th>
              </tr>
            </thead>

            <tbody>
              {materials.map((item, index) => {
                const goodStock = Number(item.qty || 0);
                const defectiveStock = Number(item.defective_stock || 0);
                const scrapStock = Number(item.scrap_stock || 0);
                const totalStock =
                  goodStock + defectiveStock + scrapStock;

                return (
                  <tr
                    key={item.item_id}
                    className={`border-b ${
                      index % 2 === 0
                        ? "bg-slate-50"
                        : "bg-white"
                    }`}
                  >
                    <td className="p-3 font-semibold capitalize">
                      {item.item_name || item.item_id}
                    </td>

                    <td className="p-3">
                      {goodStock}
                    </td>

                    <td className="p-3">
                      {defectiveStock}
                    </td>

                    <td className="p-3">
                      {scrapStock}
                    </td>

                    <td className="p-3 font-medium">
                      {totalStock}
                    </td>

                    <td className="p-3">
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusBadge(
                          defectiveStock,
                          scrapStock
                        )}`}
                      >
                        {getStatusText(
                          defectiveStock,
                          scrapStock
                        )}
                      </span>
                    </td>

                    <td className="p-3 text-sm text-gray-600">
                      {item.updated_at?.seconds
                        ? new Date(
                            item.updated_at.seconds * 1000
                          ).toLocaleString()
                        : "-"}
                    </td>
                  </tr>
                );
              })}
            </tbody>

          </table>
        </div>

        {/* P4-8: nothing is hidden without a count */}
        <div className="mt-3 space-y-1 text-sm text-gray-500">
          {stockInfo.attention.length > 0 && (
            <p>{stockInfo.attention.length} items need attention, not listed here. See Stock List.</p>
          )}
          {stockInfo.inactiveWithStock.items > 0 && (
            <p>{stockInfo.inactiveWithStock.items} disabled items with stock hidden, qty {stockInfo.inactiveWithStock.qty}</p>
          )}
          {stockInfo.junkDocs > 0 && (
            <p>{stockInfo.junkDocs} old junk records ignored (MED-22)</p>
          )}
        </div>

      </div>
    </div>
  );
}